import { useEffect, useState, useCallback } from "react";
import { Mail, RefreshCw, Loader2 } from "lucide-react";
import api from "../lib/api";

const CATEGORIES = [
  "rfq_confirmation", "internal_rfq", "supplier_rfq", "supplier_quote_received", "customer_quote",
  "quote_reminder", "order_status", "invoice", "verification", "internal_portal_action",
  "deadline_reminder", "deadline_overdue", "general",
];
const STATUSES = ["SENT", "FAILED", "LOGGED_NOT_CONFIGURED", "LOGGED_DISABLED", "SKIPPED_DUPLICATE", "SKIPPED_SUPERSEDED", "SKIPPED_NO_RECIPIENT"];
const BADGE = {
  SENT: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-600",
  LOGGED_NOT_CONFIGURED: "bg-amber-100 text-amber-700",
  LOGGED_DISABLED: "bg-amber-100 text-amber-700",
  SKIPPED_DUPLICATE: "bg-slate-100 text-slate-500",
  SKIPPED_SUPERSEDED: "bg-slate-100 text-slate-500",
  SKIPPED_NO_RECIPIENT: "bg-slate-100 text-slate-500",
};
const STATUS_HINT = {
  LOGGED_NOT_CONFIGURED: "Prepared — email provider not configured",
  LOGGED_DISABLED: "Prepared — sending deactivated in Settings",
  SKIPPED_DUPLICATE: "Skipped — identical email already handled",
  SKIPPED_SUPERSEDED: "Skipped — a more imminent reminder was sent",
  SKIPPED_NO_RECIPIENT: "Skipped — no recipient address",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const nice = (s) => (s || "").replace(/_/g, " ");

export default function EmailLog() {
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/admin/email-log", { params: { limit: 100, category: category || undefined, status: status || undefined } })
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setBusy(false));
  }, [category, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-email-log-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Email Log</h1>
        <button data-testid="email-log-refresh" onClick={load} disabled={busy}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>
      <p className="mt-2 text-[13.5px] text-slate-500">Every email the platform prepares or sends. While sending is deactivated, emails are logged here instead of being delivered.</p>

      <div className="mt-5 flex flex-wrap gap-3">
        <select data-testid="email-log-status-filter" className="pg-input !py-2.5 !w-[260px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{nice(s)}</option>)}
        </select>
        <select data-testid="email-log-category-filter" className="pg-input !py-2.5 !w-[240px]" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{nice(c)}</option>)}
        </select>
      </div>

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13px] min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">When</th><th className="px-5 py-3">Category</th><th className="px-5 py-3">To</th>
              <th className="px-5 py-3">Subject</th><th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
            {items && items.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400" data-testid="email-log-empty">
                <Mail className="mx-auto mb-2 text-slate-300" size={28} />No emails match these filters yet.
              </td></tr>
            )}
            {items && items.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`email-log-row-${e.id}`}>
                <td className="px-5 py-3 whitespace-nowrap text-slate-500">{fmt(e.created_at)}</td>
                <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-semibold bg-blue-50 text-blue-700 border border-blue-100">{nice(e.category)}</span></td>
                <td className="px-5 py-3 text-slate-500 max-w-[180px] truncate">{e.to || "—"}</td>
                <td className="px-5 py-3 text-[var(--pg-navy)] max-w-[320px] truncate" title={e.subject}>{e.subject}</td>
                <td className="px-5 py-3">
                  <span title={STATUS_HINT[e.status] || ""} className={`px-2.5 py-1 rounded-full text-[10.5px] font-[Poppins] font-semibold whitespace-nowrap ${BADGE[e.status] || "bg-slate-100 text-slate-500"}`}>{nice(e.status)}</span>
                  {e.error && <div className="mt-1 text-[11px] text-red-500 max-w-[220px]">{e.error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
